import React, { useEffect, useState } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Routes, useTypedLocation } from 'routing';
import ArticlesView from './ArticlesView';
import SingleItem from './SingleItem';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { useDispatch, useSelector } from 'react-redux';
import {
	clearInventoryConfigurationListener,
	clearInventoryItemsListener,
	clearSkuItemsListener,
	setInventoryConfigurationListener,
	setInventoryItemsListener,
	setSkuItemsListener,
} from 'actions/InventoryActions';
import * as InventorySelectors from 'selectors/InventorySelectors';
import { Box, styled, Typography } from '@mui/material';
import Spinner from 'components/Spinner';
import AddInventory from './AddArticles';
import ImportInventory from './ImportInventory';
import SkuView from './SkuView';
import SetPageTitle from 'components/Portals/SetPageTitle';
import { useTranslation } from 'services/localization';
import InventoryOnboarding from './components/InventoryOnboarding';
import TabContainer from 'components/nav/TabContainer';
import SetPageNavAction from 'components/Portals/SetPageNavAction';

const InventoryRouter = () => {
	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { state } = useTypedLocation();
	const shopId = useSelector(ShopSelectors.activeShopId)!;
	const skuItems = useSelector(InventorySelectors.skuItems);
	const inventoryConfiguration = useSelector(InventorySelectors.inventoryConfiguration);
	const inventoryItems = useSelector(InventorySelectors.inventoryItems);

	const [showOnboarding, setShowOnboarding] = useState(true);

	useEffect(() => {
		dispatch(setInventoryConfigurationListener(shopId));
		dispatch(setSkuItemsListener(shopId));
		dispatch(setInventoryItemsListener(shopId));
		return () => {
			dispatch(clearSkuItemsListener());
			dispatch(clearInventoryConfigurationListener());
			dispatch(clearInventoryItemsListener());
		};
	}, [dispatch, shopId]);

	if (!!skuItems.error || !!inventoryConfiguration.error || !!inventoryItems.error) {
		return <Typography variant="h5">{t('common:errors.errorMessage')}</Typography>;
	}

	if (skuItems.loading || inventoryConfiguration.loading || inventoryItems.loading) {
		return <Spinner />;
	}

	if (!skuItems.data || !inventoryItems.data) {
		return null;
	}

	const noExistingInventory = inventoryItems.data.length === 0 && skuItems.data.length === 0;

	return (
		<Switch>
			<Route
				exact
				path={Routes.INVENTORY_ARTICLES_ADD}
				render={() => (
					<>
						{state?.from === Routes.HOME_VIEW && (
							<SetPageNavAction
								priority={2}
								backAction={{
									to: Routes.HOME_VIEW,
									title: t('common:actions.home', 'Home'),
								}}
								quickLinks={[]}
							/>
						)}
						<AddInventory />
					</>
				)}
			/>
			<Route path={Routes.INVENTORY_ARTICLES_SINGLE} component={SingleItem} />
			<Route path={Routes.INVENTORY_IMPORT} component={ImportInventory} />
			<Route
				path={Routes.INVENTORY}
				render={() => (
					<>
						<SetPageTitle
							styling={{ fluid: true }}
							title={t('inventory.inventory', 'Inventory')}
							priority={1}
						/>
						{state?.from === Routes.HOME_VIEW && (
							<SetPageNavAction
								priority={2}
								backAction={{
									to: Routes.HOME_VIEW,
									title: t('common:actions.home', 'Home'),
								}}
								quickLinks={[]}
							/>
						)}
						{noExistingInventory && showOnboarding ? (
							<InventoryOnboarding skipOnboarding={() => setShowOnboarding(false)} />
						) : (
							<TabContainer
								basePath={Routes.INVENTORY}
								tabs={[
									{
										label: t('inventory.articlesTab', 'Articles'),
										path: 'articles',
										component: ArticlesView,
									},
									{
										label: t('inventory.skuTab', 'Stock Keeping Units'),
										path: 'skus',
										component: SkuView,
									},
								]}
								renderTabs={(tabs) => <TabsContainer>{tabs}</TabsContainer>}
							/>
						)}
					</>
				)}
			/>
			<Redirect to={Routes.INVENTORY_ARTICLES} />
		</Switch>
	);
};

const TabsContainer = styled(Box)(({ theme }) => ({
	flex: '1 1 auto',
	display: 'flex',
	flexDirection: 'column',
	padding: theme.spacing(4, 2, 0, 2),
	[theme.breakpoints.up('sm')]: {
		padding: theme.spacing(4, 4, 0, 4),
	},
	[theme.breakpoints.up('md')]: {
		padding: theme.spacing(4, 8, 0, 8),
	},
}));

export default InventoryRouter;
